import * as THREE from "three";

const createExperienceDestination = ({ accent, tech, compact }) => {
  const group = new THREE.Group();
  const routePoints = [];

  [-2.8, -1.85, -0.9, 0.05].forEach((x, index) => {
    const beacon = new THREE.Group();
    const height = 0.88 + index * 0.36;
    const column = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.18, height, 6), tech.clone());
    column.position.y = height / 2;
    const cap = new THREE.Mesh(new THREE.OctahedronGeometry(0.2, 0), accent.clone());
    cap.position.y = height + 0.12;
    const halo = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.026, 5, 24), accent.clone());
    halo.position.y = height + 0.12;
    halo.rotation.x = Math.PI / 2;
    beacon.add(column, cap, halo);
    beacon.position.set(x, -1.55 + index * 0.08, -4.4 - index * 0.28);
    group.add(beacon);
    routePoints.push(new THREE.Vector3(x, -1.42 + index * 0.24, -4.35 - index * 0.28));
  });

  const route = new THREE.CatmullRomCurve3(routePoints);
  group.add(new THREE.Mesh(new THREE.TubeGeometry(route, compact ? 24 : 48, 0.035, 5, false), accent.clone()));
  group.position.set(3.55, 0.55, 0);
  group.userData.halos = group.children.flatMap((child) => child.children || []).filter((child) => child.geometry?.type === "TorusGeometry");
  return group;
};

const createResearchDestination = ({ accent, tech, compact }) => {
  const group = new THREE.Group();
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.38, 1), tech.clone());
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.92, 0.035, 6, compact ? 36 : 64), accent.clone());
  ring.rotation.set(1.08, 0.18, -0.32);
  const instrument = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.08, 1.5, 6), tech.clone());
  instrument.rotation.z = Math.PI / 2;
  const solarGeometry = new THREE.BoxGeometry(0.58, 0.03, 0.34);
  const leftPanel = new THREE.Mesh(solarGeometry, tech.clone());
  const rightPanel = leftPanel.clone();
  leftPanel.position.x = -0.78;
  rightPanel.position.x = 0.78;
  group.add(core, ring, instrument, leftPanel, rightPanel);
  group.position.set(3.05, 0.52, -4.9);
  group.userData.ring = ring;
  return group;
};

const createProjectsDestination = ({ accent, tech }) => {
  const group = new THREE.Group();
  [0, 1, 2, 3].forEach((index) => {
    const frame = new THREE.Group();
    const frameMaterial = (index === 1 || index === 3 ? accent : tech).clone();
    const height = 1.78 - index * 0.17;
    const span = 0.78 - index * 0.07;
    const postGeometry = new THREE.BoxGeometry(0.1, height, 0.12);
    const left = new THREE.Mesh(postGeometry, frameMaterial);
    const right = left.clone();
    left.position.x = -span;
    right.position.x = span;
    const beam = new THREE.Mesh(new THREE.BoxGeometry(span * 2 + 0.1, 0.1, 0.12), frameMaterial);
    beam.position.y = height / 2;
    frame.add(left, right, beam);
    frame.position.set(index * 0.16, -0.88 + index * 0.05, -index * 0.56);
    group.add(frame);
  });
  group.position.set(3.55, 1.12, -3.25);
  return group;
};

const createSkillsDestination = ({ accent, tech }) => {
  const group = new THREE.Group();
  const nodes = [];
  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 5; column += 1) {
      const node = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.08 + ((row + column) % 2) * 0.02, 0),
        (column === 2 ? accent : tech).clone()
      );
      node.position.set(column * 0.42, row * 0.36, (row % 2) * -0.12);
      group.add(node);
      nodes.push(node.position.clone());
    }
  }

  const linePositions = [];
  nodes.forEach((position, index) => {
    if ((index + 1) % 5 !== 0) linePositions.push(position.x, position.y, position.z, nodes[index + 1].x, nodes[index + 1].y, nodes[index + 1].z);
    if (index + 5 < nodes.length) linePositions.push(position.x, position.y, position.z, nodes[index + 5].x, nodes[index + 5].y, nodes[index + 5].z);
  });
  const lineGeometry = new THREE.BufferGeometry();
  lineGeometry.setAttribute("position", new THREE.Float32BufferAttribute(linePositions, 3));
  const lineMaterial = new THREE.LineBasicMaterial({ color: tech.color, transparent: true, opacity: 0.42 });
  lineMaterial.userData.baseOpacity = 0.42;
  group.add(new THREE.LineSegments(lineGeometry, lineMaterial));
  group.position.set(2.85, 0.78, -3.85);
  group.rotation.y = 0.3;
  return group;
};

const createTestimonialsDestination = ({ accent, tech }) => {
  const group = new THREE.Group();
  [-0.7, 0.7].forEach((x) => {
    const pylon = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.16, 1.5, 5), tech.clone());
    pylon.position.set(x, -0.55, 0);
    group.add(pylon);
  });
  const signal = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.028, 5, 48, Math.PI), accent.clone());
  signal.rotation.z = Math.PI;
  signal.position.y = 0.12;
  const secondSignal = signal.clone();
  secondSignal.scale.setScalar(1.32);
  secondSignal.material = accent.clone();
  secondSignal.material.opacity *= 0.55;
  secondSignal.material.userData.baseOpacity = secondSignal.material.opacity;
  group.add(signal, secondSignal);
  group.position.set(3.25, 0.48, -3.85);
  group.userData.signal = signal;
  group.userData.secondSignal = secondSignal;
  return group;
};

const createContactDestination = ({ accent, tech, terrain }) => {
  const group = new THREE.Group();
  const marker = new THREE.Mesh(new THREE.ConeGeometry(0.22, 1.65, 5), tech.clone());
  marker.position.set(-0.5, -0.62, 0);
  const moon = new THREE.Mesh(new THREE.IcosahedronGeometry(0.42, 1), terrain.clone());
  moon.position.set(1.3, 1.05, -1.1);
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.055, 3.8, 6), accent.clone());
  beam.position.set(-0.5, 1.8, 0);
  beam.material.opacity *= 0.48;
  beam.material.userData.baseOpacity = beam.material.opacity;
  group.add(marker, moon, beam);
  group.position.set(2.7, -0.42, -5.4);
  group.userData.moon = moon;
  group.userData.beam = beam;
  return group;
};

export const createDestinations = ({ world, materials, compact }) => {
  const factories = {
    experience: () => createExperienceDestination({ ...materials, compact }),
    research: () => createResearchDestination({ ...materials, compact }),
    projects: () => createProjectsDestination(materials),
    skills: () => createSkillsDestination(materials),
    testimonials: () => createTestimonialsDestination(materials),
    contact: () => createContactDestination(materials),
  };

  const groups = Object.fromEntries(Object.entries(factories).map(([id, factory]) => [id, factory()]));
  Object.values(groups).forEach((group) => world.add(group));
  return groups;
};
